import { defineStore } from 'pinia';

/* Vuetify theme colors, which the snackbar takes as its color. */
type TNotificationType = 'error' | 'success' | 'warning';

type TNotification = {
  text: string
  type: TNotificationType | ''
  title: string
};

/* Older callers send `message` instead of `text`. */
export type TNotificationInput = Readonly<Partial<TNotification> & { message?: string }>;

const isNotificationList = (
  notification: TNotificationInput | ReadonlyArray<TNotificationInput>,
): notification is ReadonlyArray<TNotificationInput> => Array.isArray(notification);

type TNotificationState = {
  queue: TNotification[]
  activeNotification: TNotification | null
  active: boolean
};

export const useNotificationStore = defineStore('notification', {
  state: (): TNotificationState => ({
    queue: [],
    activeNotification: null,
    active: false,
  }),
  actions: {
    add(notification: TNotificationInput | ReadonlyArray<TNotificationInput>): void {
      const notifications = isNotificationList(notification) ? notification : [notification];
      this.queue.push(...notifications.map((item) => this.normalize(item)));
      if (!this.active) {
        this.showNext();
      }
    },
    set(notification: TNotificationInput | ReadonlyArray<TNotificationInput>): void {
      this.queue = [];
      this.add(notification);
    },
    notifyFailure(error: unknown, text: string): void {
      console.error(text, error);
      if (wasNotified(error)) return;
      this.set({
        title: 'Fehler', text, type: 'error',
      });
    },
    showNext(): void {
      if (this.queue.length > 0) {
        this.activeNotification = this.queue.shift() ?? null;
        this.active = true;
      } else {
        this.activeNotification = null;
        this.active = false;
      }
    },
    close(): void {
      if (!this.activeNotification) return;

      this.active = false;
      this.activeNotification = null;
      setTimeout(() => {
        this.showNext();
      }, 0);
    },
    normalize(notification: TNotificationInput): TNotification {
      return {
        text: notification.text || notification.message || '',
        type: notification.type || '',
        title: notification.title || '',
      };
    },
  },
});
